import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from '../../util/reactIntl';
import { ensureCurrentUser } from '../../util/data';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import Modal from '../Modal/Modal.js';
import { fetchCurrentUser, sendVerificationEmail } from '../../ducks/user.duck';
import { Heading, NamedLink, IconEmailSent, InlineTextButton, IconClose } from '../';
import css from './EmailVerificationModal.module.css';

/**
 * Reusable EmailVerificationModal component that automatically shows
 * when user email is not verified and handles verification logic.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.currentUser - Current user object
 * @param {boolean} props.scrollingDisabled - Whether scrolling is disabled
 * @param {Function} props.onManageDisableScrolling - Function to manage scroll disabling
 * @param {Function} props.submitVerification - Email verification submit function
 * @param {boolean} props.isVerified - Whether email is verified
 * @param {boolean} props.emailVerificationInProgress - Whether verification is in progress
 * @param {Object} props.verificationError - Verification error object
 * @param {Function} props.onClose - Optional callback when modal is closed
 * @param {Function} props.onVerified - Optional callback when email is successfully verified
 * @param {boolean} props.shouldShow - Optional override to control modal visibility
 * @param {string} props.modalId - Unique ID for the modal
 * @returns {JSX.Element} EmailVerificationModal component
 */
export const EmailVerificationModal = ({
  currentUser,
  scrollingDisabled,
  onManageDisableScrolling,
  submitVerification,
  isVerified,
  emailVerificationInProgress,
  verificationError,
  onClose,
  onVerified,
  shouldShow,
  modalId = 'EmailVerificationModal',
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refreshInProgress, setRefreshInProgress] = useState(false);
  const [verificationAttempted, setVerificationAttempted] = useState(false);
  const [sendVerificationEmailInProgress, setSendVerificationEmailInProgress] = useState(false);
  const [resendErrorMessage, setResendErrorMessage] = useState(null);
  const dispatch = useDispatch();

  const user = ensureCurrentUser(currentUser);
  const currentUserLoaded = !!user.id;
  const emailNotVerified = currentUserLoaded && !user.attributes.emailVerified;

  // Determine if modal should be shown
  const showModal = shouldShow !== undefined ? shouldShow : emailNotVerified;

  // Show modal when email is not verified
  useEffect(() => {
    if (showModal && !isModalOpen) {
      setIsModalOpen(true);
    }
  }, [showModal, isModalOpen]);

  // Close modal when email is verified
  useEffect(() => {
    if (user?.attributes?.emailVerified && isModalOpen) {
      setIsModalOpen(false);
      setVerificationAttempted(false);
      // Explicitly re-enable scrolling when modal closes
      onManageDisableScrolling(modalId, false);
      if (onVerified) {
        onVerified();
      }
    }
  }, [user?.attributes?.emailVerified, isModalOpen, onVerified, modalId, onManageDisableScrolling]);

  const handleClose = () => {
    setIsModalOpen(false);
    setVerificationAttempted(false);
    // Explicitly re-enable scrolling when modal closes
    onManageDisableScrolling(modalId, false);
    if (onClose) {
      onClose();
    }
  };

  const handleRefreshUser = async () => {
    console.log('Refreshing user data...');
    setRefreshInProgress(true);
    setVerificationAttempted(false);
    try {
      // Dispatch fetchCurrentUser to refresh user data
      // This will check if email is now verified
      console.log('Calling fetchCurrentUser...');
      await dispatch(fetchCurrentUser({ enforce: true }));
      console.log('fetchCurrentUser completed');

      // Check if email is now verified after a short delay to ensure state is updated
      setTimeout(() => {
        setVerificationAttempted(true);
      }, 500);
    } catch (error) {
      console.error('Error refreshing user data:', error);
      setVerificationAttempted(true);
    } finally {
      setRefreshInProgress(false);
    }
  };

  const handleResendVerificationEmail = async () => {
    setSendVerificationEmailInProgress(true);
    setResendErrorMessage(null);
    try {
      await dispatch(sendVerificationEmail());
      console.log('Verification email resent successfully');
    } catch (error) {
      console.error('Error resending verification email:', error);
      setResendErrorMessage('Failed to resend verification email. Please try again.');
    } finally {
      setSendVerificationEmailInProgress(false);
    }
  };

  if (!isModalOpen) {
    return null;
  }

  const { email, profile } = user.attributes;
  const emailToVerify = <strong>{email}</strong>;
  const name = profile.firstName;

  // Show verification UI when email is not verified
  // const verifyEmailUI = (
  //   <div className={css.root}>
  //     <div>
  //       <IconEmailAttention className={css.modalIcon} />
  //       <Heading as="h1" rootClassName={css.modalTitle}>
  //         <FormattedMessage id="EmailVerificationForm.verifyEmailAddress" />
  //       </Heading>

  //       <p className={css.modalMessage}>
  //         <FormattedMessage
  //           id="EmailVerificationForm.finishAccountSetup"
  //           values={{ email: emailToVerify }}
  //         />
  //       </p>

  //       {verificationAttempted && !user.attributes.emailVerified && (
  //         <div className={css.error}>
  //           <FormattedMessage id="EmailVerificationForm.verificationFailed" />
  //         </div>
  //       )}
  //     </div>

  //     <div className={css.bottomWrapper}>
  //       {/* <PrimaryButton
  //         type="button"
  //         inProgress={refreshInProgress}
  //         onClick={handleRefreshUser}
  //       >
  //         {refreshInProgress ? (
  //           <FormattedMessage id="EmailVerificationForm.verifying" />
  //         ) : (
  //           <FormattedMessage id="EmailVerificationForm.verify" />
  //         )}
  //       </PrimaryButton> */}
  //     </div>
  //   </div>
  // );

  // EmailVerificationInfo component UI replicated
  const emailVerificationInfoUI = (
    <div className={css.content}>

      <IconEmailSent className={css.modalIcon} />
      <Heading as="h1" rootClassName={css.modalTitle}>
        <FormattedMessage id="AuthenticationPage.verifyEmailTitle" values={{ name }} />
      </Heading>
      <p className={css.modalMessage}>
        <FormattedMessage id="AuthenticationPage.verifyEmailText" values={{ email }} />
      </p>

      
      {resendErrorMessage}

      <div className={css.bottomWrapper}>
        <p className={css.modalHelperText}>
          {sendVerificationEmailInProgress ? (
            <FormattedMessage id="AuthenticationPage.sendingEmail" />
          ) : (
            <FormattedMessage 
              id="AuthenticationPage.resendEmail" 
              values={{ 
                resendEmailLink: (
                  <InlineTextButton rootClassName={css.modalHelperLink} onClick={handleResendVerificationEmail}>
                    <FormattedMessage id="AuthenticationPage.resendEmailLinkText" />
                  </InlineTextButton>
                )
              }} 
            />
          )}
        </p>


        <p className={css.modalHelperText}>
          <FormattedMessage 
            id="AuthenticationPage.fixEmail" 
            values={{ 
              fixEmailLink: (
                <NamedLink className={css.modalHelperLink} name="ContactDetailsPage">
                  <FormattedMessage id="AuthenticationPage.fixEmailLinkText" />
                </NamedLink>
              )
            }} 
          />
        </p>
      </div>
    </div>
  );



  return (
    <Modal
      id={modalId}
      isOpen={isModalOpen && !user.attributes.emailVerified}
      onClose={handleClose}
      onManageDisableScrolling={onManageDisableScrolling}
    >
      <div className={css.modalWrapper}>
        {emailVerificationInfoUI}
      </div>
    </Modal>
  );
};

export default EmailVerificationModal;
